import { type RouteParams } from 'vue-router'
import type { RouteIconKey } from '@klausapp/app-navigation/types'
import i18n from '@/i18n'
import { type RouteRecord } from '@/modules/application/components/CommandPalette/utils/useRoutes'

interface RouteTranslations {
  label: string
  keywords?: string[]
  breadcrumbs?: string[]
  type?: string
  typeKey?: RouteIconKey
}

type RouteTranslationsMap = Record<string, RouteTranslations>

// Lazy getter for route translations map - prevents calling i18n.t() at module level
let _routeTranslationsMap: RouteTranslationsMap | null = null
const getRouteTranslationsMap = (): RouteTranslationsMap => {
  if (_routeTranslationsMap) return _routeTranslationsMap

  _routeTranslationsMap = {
    hub: {
      label: i18n.t('routes.hub.label'),
      typeKey: 'hub',
    },
    'coaching.sessions': {
      label: i18n.t('routes.coaching.label.sessions'),
      type: i18n.t('routes.coaching.type'),
      typeKey: 'coaching',
    },
    conversations: {
      label: i18n.t('routes.conversations.label'),
      keywords: [
        i18n.t('routes.conversations.keyword.conversations'),
        i18n.t('routes.conversations.keyword.review'),
        i18n.t('routes.conversations.keyword.filters'),
        i18n.t('routes.conversations.keyword.calibration'),
      ],
      typeKey: 'conversations',
    },
    pins: {
      label: i18n.t('routes.coaching.label.pins'),
      type: i18n.t('routes.coaching.type'),
      typeKey: 'coaching',
    },
    assignments: {
      label: i18n.t('routes.assignments.label'),
      typeKey: 'activity',
    },
    'activity.reviewsReceived': {
      label: i18n.t('routes.activity.label.reviews'),
      keywords: [i18n.t('routes.activity.keyword.my_reviews')],
      breadcrumbs: [i18n.t('routes.activity.breadcrumb.received')],
      type: i18n.t('routes.activity.type'),
      typeKey: 'activity',
    },
    'activity.reviewsGiven': {
      label: i18n.t('routes.activity.label.reviews'),
      keywords: [i18n.t('routes.activity.keyword.my_reviews')],
      breadcrumbs: [i18n.t('routes.activity.breadcrumb.given')],
      type: i18n.t('routes.activity.type'),
      typeKey: 'activity',
    },
    'activity.commentsReceived': {
      label: i18n.t('routes.activity.label.comments'),
      keywords: [i18n.t('routes.activity.keyword.my_comments')],
      breadcrumbs: [i18n.t('routes.activity.breadcrumb.received')],
      type: i18n.t('routes.activity.type'),
      typeKey: 'activity',
    },
    'activity.commentsGiven': {
      label: i18n.t('routes.activity.label.comments'),
      keywords: [i18n.t('routes.activity.keyword.my_comments')],
      breadcrumbs: [i18n.t('routes.activity.breadcrumb.given')],
      type: i18n.t('routes.activity.label.activity'),
      typeKey: 'activity',
    },
    'activity.reactionsReceived': {
      label: i18n.t('routes.activity.label.reactions'),
      breadcrumbs: [i18n.t('routes.activity.breadcrumb.received')],
      type: i18n.t('routes.activity.type'),
      typeKey: 'activity',
    },
    'activity.feedbackReceived': {
      label: i18n.t('routes.activity.label.survey_feedback'),
      breadcrumbs: [i18n.t('routes.activity.breadcrumb.received')],
      type: i18n.t('routes.activity.type'),
      typeKey: 'activity',
    },
    'activity.disputesAll': {
      label: i18n.t('routes.activity.label.all'),
      breadcrumbs: [i18n.t('routes.activity.breadcrumb.disputes')],
      type: i18n.t('routes.activity.type'),
      typeKey: 'activity',
    },
    'activity.disputesStarted': {
      label: i18n.t('routes.activity.label.started'),
      keywords: [i18n.t('routes.activity.keyword.my_disputes')],
      breadcrumbs: [i18n.t('routes.activity.breadcrumb.disputes')],
      type: i18n.t('routes.activity.type'),
      typeKey: 'activity',
    },
    'activity.disputesReceived': {
      label: i18n.t('routes.activity.label.received'),
      keywords: [i18n.t('routes.activity.keyword.my_disputes')],
      breadcrumbs: [i18n.t('routes.activity.breadcrumb.disputes')],
      type: i18n.t('routes.activity.type'),
      typeKey: 'activity',
    },
    'activity.evaluationsGiven': {
      label: i18n.t('routes.activity.label.evaluations'),
      keywords: [i18n.t('routes.activity.keyword.my_evaluations')],
      breadcrumbs: [i18n.t('routes.activity.breadcrumb.given')],
      type: i18n.t('routes.activity.type'),
      typeKey: 'activity',
    },
    'activity.evaluationsReceived': {
      label: i18n.t('routes.activity.label.evaluations'),
      keywords: [i18n.t('routes.activity.keyword.my_evaluations')],
      breadcrumbs: [i18n.t('routes.activity.breadcrumb.received')],
      type: i18n.t('routes.activity.type'),
      typeKey: 'activity',
    },
    'dashboard.looker.assignments::assignments': {
      label: i18n.t('routes.dashboard.label.assignments'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.manual_reviews::reviews': {
      label: i18n.t('routes.dashboard.label.reviews'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.manual_reviews_agent::reviews': {
      label: i18n.t('routes.dashboard.label.reviews'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.manual_reviews::reviews_limited': {
      label: i18n.t('routes.dashboard.label.reviews'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.manual_reviews_agent::reviews_limited': {
      label: i18n.t('routes.dashboard.label.reviews'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.manual_reviews::reviews_v2': {
      label: i18n.t('routes.dashboard.label.reviews'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.manual_reviews_agent::reviews_v2': {
      label: i18n.t('routes.dashboard.label.reviews'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.manual_reviews::reviews_limited_v2': {
      label: i18n.t('routes.dashboard.label.reviews'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.manual_reviews_agent::reviews_limited_v2': {
      label: i18n.t('routes.dashboard.label.reviews'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.autoqa::autoqa_accuracy': {
      label: i18n.t('routes.dashboard.label.auto_qa'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.autoqa::autoqa_accuracy_v2': {
      label: i18n.t('routes.dashboard.label.auto_qa'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.botqa::botqa': {
      label: i18n.t('routes.dashboard.label.bot_qa'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.manual_reviews::categories': {
      label: i18n.t('routes.dashboard.label.categories'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.manual_reviews_agent::categories_agent': {
      label: i18n.t('routes.dashboard.label.categories'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.surveys': {
      label: i18n.t('routes.dashboard.label.surveys'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.disputes': {
      label: i18n.t('routes.dashboard.label.disputes'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.calibration': {
      label: i18n.t('routes.dashboard.label.calibration'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.coaching': {
      label: i18n.t('routes.dashboard.label.coaching'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.coaching_activity::coaching': {
      label: i18n.t('routes.dashboard.label.coaching'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.surveys::surveys': {
      label: i18n.t('routes.dashboard.label.surveys'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.surveys_agent::surveys_agent': {
      label: i18n.t('routes.dashboard.label.surveys'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.disputes::disputes': {
      label: i18n.t('routes.dashboard.label.disputes'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.disputes_agent::disputes_agent': {
      label: i18n.t('routes.dashboard.label.disputes'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.calibration::calibration': {
      label: i18n.t('routes.dashboard.label.calibration'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.calibration_agent::calibration_agent': {
      label: i18n.t('routes.dashboard.label.calibration'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    'dashboard.looker.calibration_submit::calibration_submit': {
      label: i18n.t('routes.dashboard.label.calibration'),
      type: i18n.t('routes.dashboard.type'),
      typeKey: 'dashboard',
    },
    quizzes: {
      label: i18n.t('routes.coaching.label.quizzes'),
      type: i18n.t('routes.coaching.type'),
      typeKey: 'coaching',
    },
    'settings.ai': {
      label: i18n.t('routes.settings.label.ai'),
      breadcrumbs: [i18n.t('routes.settings.breadcrumb.account')],
      keywords: [i18n.t('routes.settings.keyword.settings')],
      type: i18n.t('routes.settings.type'),
      typeKey: 'settings',
    },
    'settings.ai.create': {
      label: i18n.t('routes.settings.label.new_ai_insight'),
      breadcrumbs: [i18n.t('routes.settings.breadcrumb.account'), i18n.t('routes.settings.breadcrumb.ai')],
      keywords: [i18n.t('routes.settings.keyword.settings')],
      type: i18n.t('routes.settings.type'),
      typeKey: 'settings',
    },
    'settings.connections': {
      label: i18n.t('routes.settings.label.connections'),
      keywords: [i18n.t('routes.settings.keyword.integrations'), i18n.t('routes.settings.keyword.helpdesk')],
      breadcrumbs: [i18n.t('routes.settings.breadcrumb.account')],
      type: i18n.t('routes.settings.type'),
      typeKey: 'settings',
    },
    'settings.notifications': {
      label: i18n.t('routes.settings.label.notifications'),
      breadcrumbs: [i18n.t('routes.settings.breadcrumb.account')],
      type: i18n.t('routes.settings.type'),
      typeKey: 'settings',
    },
    'settings.reviews.categories': {
      label: i18n.t('routes.settings.label.reviews_categories'),
      breadcrumbs: [i18n.t('routes.settings.breadcrumb.account'), i18n.t('routes.settings.breadcrumb.reviews')],
      keywords: [i18n.t('routes.settings.keyword.settings')],
      type: i18n.t('routes.settings.type'),
      typeKey: 'settings',
    },
    'settings.reviews.rootCauses': {
      label: i18n.t('routes.settings.label.reviews_rootcauses'),
      breadcrumbs: [i18n.t('routes.settings.breadcrumb.account'), i18n.t('routes.settings.breadcrumb.reviews')],
      keywords: [i18n.t('routes.settings.keyword.settings')],
      type: i18n.t('routes.settings.type'),
      typeKey: 'settings',
    },
    'settings.reviews.scorecards': {
      label: i18n.t('routes.settings.label.reviews_scorecards'),
      breadcrumbs: [i18n.t('routes.settings.breadcrumb.account')],
      keywords: [i18n.t('routes.settings.keyword.settings')],
      type: i18n.t('routes.settings.type'),
      typeKey: 'settings',
    },
    'settings.reviews.scorecards.new': {
      label: i18n.t('routes.settings.label.new_scorecard'),
      breadcrumbs: [i18n.t('routes.settings.breadcrumb.account'), i18n.t('routes.settings.breadcrumb.reviews')],
      keywords: [i18n.t('routes.settings.keyword.settings')],
      type: i18n.t('routes.settings.type'),
      typeKey: 'settings',
    },
    'settings.profile': {
      label: i18n.t('routes.settings.label.general'),
      breadcrumbs: [i18n.t('routes.settings.breadcrumb.account')],
      keywords: [
        i18n.t('routes.settings.keyword.organization'),
        i18n.t('routes.settings.keyword.account'),
        i18n.t('routes.settings.keyword.company'),
      ],
      type: i18n.t('routes.settings.type'),
      typeKey: 'settings',
    },
    'settings.subscription': {
      label: i18n.t('routes.settings.label.subscription'),
      keywords: [
        i18n.t('routes.settings.keyword.payment'),
        i18n.t('routes.settings.keyword.seats'),
        i18n.t('routes.settings.keyword.billing'),
      ],
      breadcrumbs: [i18n.t('routes.settings.breadcrumb.account')],
      type: i18n.t('routes.settings.type'),
      typeKey: 'settings',
    },
    'tasks.assignments': {
      label: i18n.t('routes.tasks.label.assignments'),
      type: i18n.t('routes.tasks.type'),
      typeKey: 'tasks',
    },
    'tasks.calibrations': {
      label: i18n.t('routes.tasks.label.calibrations'),
      type: i18n.t('routes.tasks.type'),
      typeKey: 'tasks',
    },
    'profile.general': {
      label: i18n.t('routes.profile_settings.label.general'),
      keywords: [
        i18n.t('routes.profile_settings.keyword.email'),
        i18n.t('routes.profile_settings.keyword.e_mail'),
        i18n.t('routes.profile_settings.keyword.user'),
      ],
      breadcrumbs: [i18n.t('routes.profile_settings.breadcrumb.personal')],
      type: i18n.t('routes.profile_settings.type'),
      typeKey: 'settings',
    },
    'profile.templates': {
      label: i18n.t('routes.profile_settings.label.templates'),
      keywords: [i18n.t('routes.profile_settings.keyword.templates')],
      breadcrumbs: [i18n.t('routes.profile_settings.breadcrumb.personal')],
      type: i18n.t('routes.profile_settings.type'),
      typeKey: 'settings',
    },
    'profile.templates.create': {
      label: i18n.t('routes.profile_settings.label.create_template'),
      keywords: [i18n.t('routes.profile_settings.keyword.templates')],
      breadcrumbs: [
        i18n.t('routes.profile_settings.breadcrumb.personal'),
        i18n.t('routes.profile_settings.breadcrumb.templates'),
      ],
      type: i18n.t('routes.profile_settings.type'),
      typeKey: 'settings',
    },
    'profile.goal': {
      label: i18n.t('routes.profile_settings.label.goal'),
      keywords: [i18n.t('routes.profile_settings.keyword.review_goal')],
      breadcrumbs: [i18n.t('routes.profile_settings.breadcrumb.personal')],
      type: i18n.t('routes.profile_settings.type'),
      typeKey: 'settings',
    },
    'profile.notifications': {
      label: i18n.t('routes.profile_settings.label.notifications'),
      breadcrumbs: [i18n.t('routes.profile_settings.breadcrumb.personal')],
      type: i18n.t('routes.profile_settings.type'),
      typeKey: 'settings',
    },
  }

  return _routeTranslationsMap
}

export const getRouteTranslations = (r: RouteRecord, params?: RouteParams): RouteTranslations | null => {
  if (!r.name) return null

  let key = r.name.toString()
  if (key === 'dashboard.looker' && params?.id) {
    key += `.${params.id.toString()}`
  }

  const routeTranslationsMap = getRouteTranslationsMap()
  return routeTranslationsMap[key]
}
